import type { LessonEndingSoonEvent, LessonFinishedEvent } from "../types/events";

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEcho } from "@laravel/echo-react";

import { useClassroomStore } from "../store/useClassroomStore";

import LessonEndedDialog from "./LessonEndedDialog";
import { LessonExtensionDialog } from "./LessonExtensionDialog";

import { toast } from "@/utils/customToast";
import useUserStore from "@/features/auth/store/useUserStore";
import PATHS from "@/routes/paths";
import { useIsMobile } from "@/hooks/use-mobile";

export default function LessonEventsHandler() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(["classroom", "common"]);
  const { user } = useUserStore();
  const isMobile = useIsMobile();

  // State for lesson ended dialog
  const [showLessonEndedDialog, setShowLessonEndedDialog] = useState(false);
  const [lessonEndedData, setLessonEndedData] = useState<LessonFinishedEvent | null>(null);

  const {
    extensionDialogOpen,
    setExtensionDialogOpen,
    extensionInfo,
    setExtensionInfo,
    setLessonEndTime,
    videoRoomData,
  } = useClassroomStore();

  // Handle lesson ended dialog confirmation
  const handleLessonEndedConfirm = () => {
    setShowLessonEndedDialog(false);
    setLessonEndedData(null);
  };

  // Handle lesson ended dialog close
  const handleLessonEndedClose = () => {
    setShowLessonEndedDialog(false);
    setLessonEndedData(null);
  };

  // Lesson ending soon event listener
  const { channel } = useEcho(
    `lesson.${videoRoomData && videoRoomData.custom_room_id}`,
    ".lesson.ending-soon",
    (data: LessonEndingSoonEvent) => {
      console.log("Lesson ending soon event:", data);

      const currentMeetingId = useClassroomStore.getState().meetingId;

      if (currentMeetingId) {
        setExtensionInfo({ roomId: currentMeetingId, eventData: data });
      }

      // Set lesson end time for countdown
      setLessonEndTime(data.lesson_end_time);

      if (data.allow_extension) {
        // Different descriptions for tutors vs students
        const description =
          user?.role === "tutor"
            ? isMobile
              ? t("classroom:lessonEndingSoonDescriptionMobile")
              : t("classroom:lessonEndingSoonDescriptionDesktop")
            : isMobile
              ? t("classroom:lessonEndingSoonDescriptionStudentMobile")
              : t("classroom:lessonEndingSoonDescriptionStudentDesktop");

        toast.important(t("classroom:lessonEndingSoon"), {
          description,
          duration: 10000,
          audio: true,
          position: "bottom-right",
        });
      } else {
        toast.important(t("classroom:lessonEndingSoonSimple"), {
          duration: 10000,
          audio: true,
        });
      }
    },
    [videoRoomData, isMobile, user],
  );

  // Listen for lesson extension whispers using the existing channel
  useEffect(() => {
    if (channel()) {
      channel().listenForWhisper("lessonExtended", (data: any) => {
        console.log("Received lesson extended whisper:", data);

        const store = useClassroomStore.getState();

        // Update lesson end time for all participants
        store.setLessonEndTime(data.newEndTime);

        toast.success(t("classroom:lessonExtendedTitle"), {
          description: t("classroom:lessonExtendedDescription", {
            minutes: data.extensionMinutes,
            name: data.extendedBy.name,
          }),
          duration: 5000,
        });
      });

      return () => {
        channel().stopListeningForWhisper("lessonExtended");
      };
    }
  }, [channel, t]);

  useEffect(() => {
    const interval = setInterval(() => {
      // @ts-ignore
      if (channel().pusher?.connection.state !== "connected" && videoRoomData) {
        channel().subscribe();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [channel()]);

  // Lesson finished event listener
  useEcho(
    `lesson.${videoRoomData && videoRoomData.custom_room_id}`,
    ".lesson.finished",
    (data: LessonFinishedEvent) => {
      console.log("Lesson finished event:", data);

      // Immediately set lesson ended flag to prevent PiP mode
      const { setLessonEnded, setIsPipMode } = useClassroomStore.getState();

      setLessonEnded(true);
      setIsPipMode(false);

      // Clear extension info on lesson finished
      setExtensionInfo(null);

      // Store lesson ended data for the dialog
      setLessonEndedData(data);

      // Navigate to lesson details page immediately
      if (user && workspaceId && videoRoomData && videoRoomData.custom_room_id) {
        const detailsPath =
          user.role === "tutor"
            ? `${PATHS.TUTOR.WORKSPACES.WORKSPACES}/${workspaceId}/lessons/${videoRoomData.custom_room_id}/details`
            : `${PATHS.STUDENT.WORKSPACES.WORKSPACES}/${workspaceId}/lessons/${videoRoomData.custom_room_id}/details`;

        navigate(detailsPath);

        // Show dialog after navigation completes
        setTimeout(() => {
          setShowLessonEndedDialog(true);
        }, 500);

        // Clean up meeting very gradually to prevent conflicts
        setTimeout(() => {
          const { setShouldMaintainConnection, setIsJoined, setView } =
            useClassroomStore.getState();

          // Stop maintaining connection first
          setShouldMaintainConnection(false);

          // Set not joined
          setIsJoined(false);

          // Reset view
          setView("prejoin");
        }, 2000);

        // Leave meeting last to prevent any navigation issues
        setTimeout(() => {
          if ((window as any).leaveMeeting) {
            console.log("Leaving meeting due to lesson finished event");
            (window as any).leaveMeeting();
          }

          // Clear meeting data after leaving
          setTimeout(() => {
            const { clearMeetingData } = useClassroomStore.getState();

            clearMeetingData();
          }, 1000);
        }, 3000);
      }
    },
    [videoRoomData],
  );

  return (
    <>
      {/* Lesson Ended Dialog */}
      <LessonEndedDialog
        extensionMinutes={lessonEndedData?.extension_minutes}
        isOpen={showLessonEndedDialog}
        wasExtended={lessonEndedData?.was_extended}
        onClose={handleLessonEndedClose}
        onConfirm={handleLessonEndedConfirm}
      />

      {/* Lesson Extension Dialog */}
      {workspaceId && videoRoomData && (
        <LessonExtensionDialog
          eventData={extensionInfo?.eventData ?? null}
          isOpen={extensionDialogOpen}
          lessonId={videoRoomData.custom_room_id}
          workspaceId={workspaceId}
          onClose={() => setExtensionDialogOpen(false)}
        />
      )}
    </>
  );
}
